// Lightweight in-memory cache shared across pages.
// Avoids redundant fetches when the user bounces between Home / My Feed / Profile.
// Stale-while-revalidate: components show cached data instantly, then refresh.
//
// Persistent-shell merge (Iter-57): added cached keys for /videos/featured,
// /videos/trending and per-profile channel-chat. With these in place the
// Home → Profile → Home → MyFeed loop never re-fetches the same payload
// within the 30-second TTL window, eliminating the last visible flicker
// when bouncing between player surfaces.

const _appCache = {
  videos: { data: null, ts: 0 },         // /api/videos
  followingFeed: { data: null, ts: 0 },  // /api/feed/following
  featured: { data: null, ts: 0 },       // /api/videos/featured — global
  trending: { data: null, ts: 0 },       // /api/videos/trending?hours=6
  profiles: {},                          // { username -> { data, ts } } — plain object so it isn't shadowed by the Map lucide icon
  channelChats: {},                      // { username -> { data, ts } }
};

const CACHE_TTL_MS = 30 * 1000; // 30 s

// Dispatch table — keeps cacheGet/cacheSet short, prevents accidental
// new top-level keys from typos.
const _PREFIXED_BUCKETS = {
  "profile:": "profiles",
  "channelChat:": "channelChats",
};

const _bucketFor = (key) => {
  for (const [prefix, bucket] of Object.entries(_PREFIXED_BUCKETS)) {
    if (key.startsWith(prefix)) {
      return { bucket: _appCache[bucket], id: key.slice(prefix.length) };
    }
  }
  return null;
};

export const cacheGet = (key) => {
  const ref = _bucketFor(key);
  const slot = ref ? ref.bucket[ref.id] : _appCache[key];
  if (!slot || !slot.data) return null;
  return Date.now() - slot.ts < CACHE_TTL_MS ? slot.data : null;
};

export const cacheSet = (key, data) => {
  const ref = _bucketFor(key);
  if (ref) {
    ref.bucket[ref.id] = { data, ts: Date.now() };
  } else if (_appCache[key]) {
    _appCache[key] = { data, ts: Date.now() };
  }
};

export const cacheInvalidate = (key) => {
  const ref = _bucketFor(key);
  if (ref) {
    delete ref.bucket[ref.id];
  } else if (_appCache[key]) {
    _appCache[key] = { data: null, ts: 0 };
  }
};
